import React, { useLayoutEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Flex, Section, FlexLeftCol } from "styles/global-style";
import styled from "styled-components";

import withTracker from "utils/withTracker";
import ninjaGif from "imgs/ninja.gif";
import check from "imgs/check.png";

const FeatureSection = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  margin: 80px auto;
  padding: 0 40px;

  @media (max-width: 900px) {
    flex-direction: ${(props) => (props.$reverse ? "column-reverse" : "column")};
    gap: 40px;
    margin: 60px auto;
    padding: 0 20px;
  }
`;

const FeatureImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const FeatureText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 600px;

  p {
    font-size: 18px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }

  @media (max-width: 900px) {
    text-align: center;

    h2 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const SubtitleBox = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 24px 40px;
  margin-top: -70px;
  margin-bottom: 170px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;

  h3 {
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
    text-align: center;
    max-width: 800px;
  }

  @media (max-width: 900px) {
    padding: 20px 30px;
  }
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 180px auto;
  padding: 0 40px;
  gap: 20px;
  max-width: 600px;

  h2 {
    margin: 0;
  }

  p {
    font-size: 18px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }

  @media (max-width: 900px) {
    margin: 80px auto;
    padding: 0 20px;
    gap: 20px;

    h2 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const SignUpButton = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #32cd32 0%, #228b22 100%);
  color: #fff;
  width: 300px;
  height: 20px;
  border-radius: 5px;
  cursor: pointer;
  padding: 16px 55px 18px 55px;
  margin: 0;
  margin-top: 10px;
  font-size: 20px;
  opacity: 1;
  user-select: none;
  transform: ${(props) => (props.$down ? "scale(0.97)" : "scale(1)")};
  font-weight: bold;
  transition: transform 0.1s ease, background 0.2s ease, box-shadow 0.2s ease;
  border: none;
  text-decoration: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  b {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(0, 0, 0, 0.4);
  }

  &:hover {
    background: linear-gradient(135deg, #3dd33d 0%, #2da02d 100%);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const TimelinePreviewWrapper = styled.div`
  width: 100%;
  margin-left: 0 !important;
  padding-left: 0 !important;
  position: relative;
  left: 0;
`;

const TimelineContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-left: 0;
  padding-left: 0;
`;

const NamesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gridSpaceY}px;
  padding-bottom: 2px;
  padding-top: 25px;
  position: relative;
  margin-left: 0;
  padding-left: 0;
`;

const NameRow = styled.div`
  background: rgb(170, 170, 170);
  height: ${(props) => props.$boxSize}px;
  width: ${(props) => props.$longestNameWidth}px;
  border-radius: 0px 3px 3px 0px;
  color: black !important;
  font-size: ${(props) => typeof props.$fontSize === 'number' ? props.$fontSize + 'rem' : (props.$fontSize || "0.9rem")};
  font-weight: 500;
  padding-left: ${(props) => props.$paddingLeft || "9.6px"};
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-left: 0;
  padding-left: 8px;
  box-sizing: border-box;
`;

const DatesRow = styled.div`
  position: relative;
  height: ${(props) => props.$boxSize}px;
  margin-bottom: ${(props) => props.$gridSpaceY}px;
`;

const DotsContainer = styled.div`
  position: relative;
  height: ${(props) => props.$rowCount * (props.$boxSize + props.$gridSpaceY)}px;
  flex: 1;
  margin-top: 0;
`;

const Dot = styled.div`
  position: absolute;
  left: ${(props) => props.$left}px;
  top: ${(props) => props.$row * (props.$boxSize + props.$gridSpaceY)}px;
  width: ${(props) => props.$boxSize}px;
  height: ${(props) => props.$boxSize}px;
  background: ${(props) => props.$color || "limegreen"};
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: ${(props) => (props.$hovering ? "2px solid white" : "none")};
  transform: ${(props) => (props.$down ? "scale(0.95)" : "scale(1)")};
  transition: transform 0.1s ease;
  z-index: 1;
  
  &:hover {
    outline: 2px solid white;
  }
`;

const Pill = ({ text, color }) => {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: 120, height: 52, clipPath: "polygon(0 0, 100% 0, 100% calc(110% - 15px), 0 100%)", background: color }}>
      <h4 style={{ paddingBottom: 10 }}>{text}</h4>
    </div>
  );
};

function Home() {
  const navigate = useNavigate();
  const { hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      const param = hash.split("=");
      if (param[0] === "#recovery_token") navigate("/recover/" + param[1]);
    }
  }, [hash, navigate]);

  return (
    <>
      <Section>
        <img src={ninjaGif} alt="Peepz.io" style={{ width: 500, maxWidth: "70%", marginTop: -300 }} />
        <h1 style={{maxWidth: 700, textAlign: "center", lineHeight: 1, marginTop: -50}}>Keep it simple, team!</h1>
      </Section>
      <SubtitleBox>
        <h3 style={{ margin: 0, textAlign: "center", lineHeight: 0.9, fontWeight: 300 }}>
          <i>A project management tool for the actual target users - the team.
          <br />
          Core components in a simplistic package.</i>
        </h3>
      </SubtitleBox>

      <Flex style={{ justifyContent: "center", marginTop: -170, marginBottom: 50 }}>
        <Pill text="Team" color="purple" />
        <Pill text="Target" color="orange" />
        <Pill text="Focus" color="limegreen" />
      </Flex>

      <FeatureSection style={{ paddingLeft: 0, marginLeft: 0, maxWidth: "84%", gap: "60px", marginTop: 160 }}>
        <FeatureImage style={{ flex: "1 1 auto", minWidth: 0, justifyContent: "flex-start", paddingLeft: 0, marginLeft: 0, overflowX: "clip" }}>
          <TimelinePreviewComponent />
        </FeatureImage>
        <FeatureText style={{ flex: "0 0 auto", alignSelf: "flex-start" }}>
          <h2>Team availability</h2>
          <p>
            We live complex lives, working everywhere and at different times. At the same time we're usually a part of a team (or multiple), and the way teams move about is complex. Let your team know when you're available and where you'll be.
          </p>
        </FeatureText>
      </FeatureSection>

      <FeatureSection $reverse style={{ paddingRight: 0, marginRight: 0, maxWidth: "84%", gap: "60px", marginTop: "180px" }}>
        <FeatureText style={{ flex: "0 0 auto", alignSelf: "flex-start" }}>
          <h2>Producer monkey work</h2>
          <p>
            If we know the teams availibility, then it's monkey work to figure out how much we should take on in sprints or towards goals. Basically have a clever digital monkey that can help us avoid <span style={{ background: "#ff4444", color: "#fff", borderRadius: "5px", padding: "0px 8px", margin: "0px 2px", display: "inline-block" }}><b>overcomittment</b></span> and hence overtime, and in general improve guestimation accuracy for making milestones.
          </p>
        </FeatureText>
        <FeatureImage style={{ flex: "1 1 auto", minWidth: 0, justifyContent: "flex-end", paddingRight: 0, marginRight: 0 }}>
          <WorkPreviewComponent />
        </FeatureImage>
      </FeatureSection>

      <CTASection>
        <h2>Built by game developers</h2>
        <p>
          We built this because we needed it ourselves. After years of wrestling with tools that didn't fit how we actually work, we decided to make something that does. No fancy features you'll never use, no corporate bloat. Just the stuff that helps teams ship better games.
        </p>
        <SignUpButton onClick={() => navigate("/join")}>
          <b>Try it, it's free!</b>
        </SignUpButton>
      </CTASection>
    </>
  );
}

// 7 made up names (removed 5 rows) - moved outside component to prevent recreation on each render
const TIMELINE_PREVIEW_NAMES = [
  "Charlie K",
  "Alex M",
  "Sam R",
  "Jordan L",
  "Taylor B",
  "Morgan P",
  "Casey D"
];

const TimelinePreviewComponent = () => {
  // Scale everything by 1.2 (20% larger)
  const BOX_SIZE = 22;
  const GRID_SPACE_X = 3;
  const GRID_SPACE_Y = 3;
  const GRID_SPACE_WEEKEND = 6 * 1.2;
  const FONT_WIDTH = 10 * 1.2;
  const longestName = 10;
  const longestNameWidth = longestName * FONT_WIDTH;

  const [hoveredDot, setHoveredDot] = useState(null);
  const [pressedDot, setPressedDot] = useState(null);

  // Generate dates starting from today minus 10 days, grouped by weeks (5 days per week)
  const { dates, dots, monthIndexes } = useMemo(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 10);
    
    // Find the Monday of the week containing startDate
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const mondayDate = new Date(startDate);
    mondayDate.setDate(startDate.getDate() - daysToMonday);
    
    const datesArray = [];
    const dotsArray = [];
    let x = BOX_SIZE + GRID_SPACE_X;
    const numWeeks = 6;
    let currentDate = new Date(mondayDate);
    
    // Track month changes
    let lastMonth = currentDate.getMonth();
    const monthIndexesArray = [];
    
    // Use deterministic "random" values for consistent preview
    let seed = 12345;
    const seededRandom = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    
    let dayIndex = 0;
    for (let week = 0; week < numWeeks; week++) {
      // Each week has 5 days (Monday-Friday)
      for (let dayInWeek = 0; dayInWeek < 5; dayInWeek++) {
        const dayOfWeek = currentDate.getDay();
        
        // Track month changes
        if (currentDate.getMonth() !== lastMonth) {
          const monthName = currentDate.toLocaleString('default', { month: 'long' });
          monthIndexesArray.push({ 
            index: dayIndex, 
            monthName: monthName.charAt(0).toUpperCase() + monthName.slice(1), 
            year: currentDate.getFullYear() 
          });
          lastMonth = currentDate.getMonth();
        }
        
        datesArray.push({
          date: new Date(currentDate),
          left: x,
          dayOfWeek: dayOfWeek,
          dayIndex: dayIndex
        });
        
        x += BOX_SIZE + GRID_SPACE_X;
        currentDate.setDate(currentDate.getDate() + 1);
        dayIndex++;
      }
      
      // Add weekend spacing after each week (skip Saturday and Sunday)
      if (week < numWeeks - 1) {
        x += GRID_SPACE_WEEKEND;
        currentDate.setDate(currentDate.getDate() + 2); // Skip Saturday and Sunday
      }
    }
    
    // Generate dots for each person
    // Left side: fully filled with green dots (first 10 days)
    // Right side: progressively sparser as you go right
    const leftSideDays = 10;
    const totalDays = datesArray.length;
    const rightSideDays = totalDays - leftSideDays;
    
    TIMELINE_PREVIEW_NAMES.forEach((name, rowIndex) => {
      datesArray.forEach((dateSlot) => {
        if (dateSlot.dayIndex < leftSideDays) {
          // Left side: all green dots, continuous
          dotsArray.push({
            row: rowIndex,
            left: dateSlot.left,
            color: "limegreen",
            hasCheck: false,
            dayIndex: dateSlot.dayIndex
          });
        } else {
          // Right side: progressively sparser based on position
          // Calculate sparsity factor: 0.9 at start, decreasing to ~0.3 at the end
          const positionRatio = (dateSlot.dayIndex - leftSideDays) / rightSideDays;
          const sparsityFactor = 0.9 - (positionRatio * 0.6); // Goes from 0.9 to 0.3
          
          // Only add dot if random value is below sparsity factor
          if (seededRandom() < sparsityFactor) {
            let color = "limegreen";
            let hasCheck = false;
            
            const rand = seededRandom();
            if (rand > 0.92) {
              color = "rgb(91, 192, 222)";
              hasCheck = true;
            } else if (rand > 0.88) {
              color = "#ff8c8c";
            } else if (rand > 0.84) {
              color = "rgb(241, 194, 50)";
            }
            
            dotsArray.push({
              row: rowIndex,
              left: dateSlot.left,
              color: color,
              hasCheck: hasCheck,
              dayIndex: dateSlot.dayIndex
            });
          }
        }
      });
    });
    
    return { dates: datesArray, dots: dotsArray, monthIndexes: monthIndexesArray };
  }, [GRID_SPACE_WEEKEND]);

  return (
    <TimelinePreviewWrapper>
      <TimelineContainer>
        <NamesColumn $gridSpaceY={GRID_SPACE_Y}>
          {TIMELINE_PREVIEW_NAMES.map((name, index) => (
            <NameRow 
              key={index}
              $boxSize={BOX_SIZE}
              $longestNameWidth={longestNameWidth}
              $fontSize={`${0.7 * 1.2}rem`}
              $paddingLeft={8 * 1.2}
            >
              {name}
            </NameRow>
          ))}
        </NamesColumn>
        
        <div style={{ position: "relative", width: "100%", display: "inline-block", marginLeft: -10, minWidth: "600px" }}>
          <DatesRow $boxSize={BOX_SIZE} $gridSpaceY={GRID_SPACE_Y}>
            {dates.map((dateSlot, j) => {
              const showMonth = monthIndexes.find((mon) => mon.index === j);
              // Always show month/year for the first date (leftmost)
              const isFirstDate = j === 0;
              const firstDateMonth = isFirstDate ? dates[0].date.toLocaleString('default', { month: 'long' }) : null;
              const firstDateYear = isFirstDate ? dates[0].date.getFullYear() : null;
              const firstDateMonthName = firstDateMonth ? firstDateMonth.charAt(0).toUpperCase() + firstDateMonth.slice(1) : null;
              
              return (
                <div key={"date" + j} style={{ position: "absolute", left: dateSlot.left, width: BOX_SIZE, userSelect: "none" }}>
                  {(showMonth || isFirstDate) && (
                    <FlexLeftCol gap={0} style={{ position: "absolute", transform: "translateY(-100%)", textShadow: "rgb(34, 34, 34) 1px 1px 3px", marginLeft: 1 }}>
                      <h5 style={{ fontSize: 12 * 1.2, marginBottom: -4 }}>{showMonth ? showMonth.year : firstDateYear}</h5>
                      <h4 style={{ fontSize: 22 * 1.2 }}>{showMonth ? showMonth.monthName : firstDateMonthName}</h4>
                    </FlexLeftCol>
                  )}
                  <div style={{ fontSize: 0.6 * 1.2 + "rem", textAlign: "center", opacity: 0.4 }}>
                    {dateSlot.date.getUTCDate()}
                  </div>
                </div>
              );
            })}
          </DatesRow>
          
          <DotsContainer $rowCount={TIMELINE_PREVIEW_NAMES.length} $boxSize={BOX_SIZE} $gridSpaceY={GRID_SPACE_Y} style={{ marginTop: 0 }}>
            {dots.map((dot, index) => (
              <Dot
                key={`dot-${dot.row}-${dot.dayIndex}`}
                $row={dot.row}
                $left={dot.left}
                $color={dot.color}
                $boxSize={BOX_SIZE}
                $gridSpaceY={GRID_SPACE_Y}
                $hovering={hoveredDot === index}
                $down={pressedDot === index}
                onMouseEnter={() => setHoveredDot(index)}
                onMouseLeave={() => {
                  setHoveredDot(null);
                  setPressedDot(null);
                }}
                onMouseDown={() => setPressedDot(index)}
                onMouseUp={() => setPressedDot(null)}
                onClick={() => {
                  // Make it interactive - could show a tooltip or change color
                  console.log(`Clicked dot for ${TIMELINE_PREVIEW_NAMES[dot.row]} on day ${dot.dayIndex}`);
                }}
              >
                {dot.hasCheck && (
                  <img 
                    src={check} 
                    alt="check" 
                    style={{ height: "40%", opacity: 0.5 }} 
                  />
                )}
              </Dot>
            ))}
          </DotsContainer>
        </div>
      </TimelineContainer>
    </TimelinePreviewWrapper>
  );
};

const WorkPreviewWrapper = styled.div`
  width: 100%;
  margin-left: 0 !important;
  padding-left: 0 !important;
  position: relative;
  left: 0;
`;

const WorkPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-left: 0;
  padding-left: 0;
`;

const ColumnsContainer = styled.div`
  display: flex;
  gap: ${(props) => props.$scale * 8}px;
  align-items: flex-start;
`;

const ColumnWrapper = styled.div`
  flex: 1;
  min-width: ${(props) => props.$scale * 180}px;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$scale * 2}px;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${(props) => props.$scale * 6}px;
  background: #ffffff44;
  backdrop-filter: blur(2px);
  opacity: 0.6;
  font-size: ${(props) => props.$scale * 10}px;
  height: ${(props) => props.$scale * 20}px;
  border-radius: ${(props) => props.$scale * 2}px;
  box-shadow: #000000 1px 1px 3px;
  font-weight: bold;
  user-select: none;
`;

const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$scale * 3}px;
  min-height: ${(props) => props.$scale * 100}px;
`;

const TaskCard = styled.div`
  position: relative;
  background: ${(props) => props.$isOvercommitted ? "#ff4444" : "#fff"};
  border-radius: ${(props) => props.$scale * 5}px;
  padding: ${(props) => props.$scale * 6}px ${(props) => props.$scale * 11}px;
  margin: ${(props) => props.$scale * 3}px;
  user-select: none;
  min-height: ${(props) => props.$scale * 19}px;
  cursor: pointer;
  box-shadow: rgba(0, 0, 0, 0.667) 1px 3px 5px;
  color: ${(props) => props.$isOvercommitted ? "#fff" : "black"};
  display: flex;
  align-items: center;
  font-size: ${(props) => props.$scale * 12}px;
  font-weight: ${(props) => props.$isOvercommitted ? "bold" : "normal"};
  transition: transform 0.2s ease;
  transform: scale(1);

  &:hover {
    transform: scale(1.05);
  }
`;

const WorkPreviewComponent = () => {
  const SCALE = 1.5; // Scale up similar to timeline preview
  
  const columns = [
    { id: "TODO", title: "TODO" },
    { id: "IN_PROGRESS", title: "IN PROGRESS" },
    { id: "REVIEW", title: "REVIEW" }
  ];

  // Define tasks for each column - some marked as overcommitted
  const tasks = {
    TODO: [
      { id: 1, title: "Design new feature", isOvercommitted: false },
      { id: 2, title: "Update API docs", isOvercommitted: false },
      { id: 3, title: "Refactor auth module", isOvercommitted: true }, // Overcommitted
    ],
    IN_PROGRESS: [
      { id: 4, title: "Fix login bug", isOvercommitted: false },
      { id: 5, title: "Optimize database queries", isOvercommitted: true }, // Overcommitted
    ],
    REVIEW: [
      { id: 6, title: "Code review PR #123", isOvercommitted: false },
    ]
  };

  return (
    <WorkPreviewWrapper>
      <WorkPreviewContainer>
        <ColumnsContainer $scale={SCALE}>
          {columns.map((col) => (
            <ColumnWrapper key={col.id} $scale={SCALE}>
              <ColumnHeader $scale={SCALE}>
                {col.title}
              </ColumnHeader>
              <ColumnContent $scale={SCALE}>
                {tasks[col.id]?.map((task) => (
                  <TaskCard 
                    key={task.id} 
                    $scale={SCALE}
                    $isOvercommitted={task.isOvercommitted}
                  >
                    {task.title}
                  </TaskCard>
                ))}
              </ColumnContent>
            </ColumnWrapper>
          ))}
        </ColumnsContainer>
      </WorkPreviewContainer>
    </WorkPreviewWrapper>
  );
};

export default withTracker(Home);
